import Icon from './Icon';

/**
 * A glyph for every figure the duel can ask about, and for the two directions.
 *
 * The SECOND family, and `ControlIcons` predicted it: "when a second family
 * arrives (a class or an origin glyph, say) it gets its own file". This is that
 * file, and the seam is real — every glyph over there OPERATES the app, and
 * every glyph in here is a SUBJECT. One of them changes what the app does; the
 * other says what a number is about.
 *
 * They exist because the duel has to be playable by somebody who cannot read the
 * question (SPEC-007 D-36). A nine-year-old, or anybody with no English, can
 * play a game whose question is a speedometer and an up arrow; they cannot play
 * one whose question is "which one is faster flat out?". So the pictogram is the
 * question and the words beside it are the caption, which is the opposite of the
 * arrangement everywhere else in this product.
 *
 * That puts a real constraint on the drawings: each has to be recognisable at
 * 28px to somebody who has never seen this app, WITHOUT its label. Where a glyph
 * could only be understood by reading the word next to it, it is the wrong
 * glyph — a stopwatch is a stopwatch to everybody, and a "0–100" numeral is a
 * spec sheet.
 *
 * Drawn in the shared `Icon` frame: 1.75px strokes, square caps, mitred joins,
 * per §10. Shape names and props are the contract; the path data is not.
 */

/** MORE — the direction chip, doubled so it reads as a quantity and not a step. */
export function MoreIcon(props) {
    return (
        <Icon {...props}>
            <path d="m5 13.5 7-7 7 7M5 19l7-7 7 7" />
        </Icon>
    );
}

/**
 * LESS.
 *
 * `MoreIcon` upside down, and DRAWN rather than transformed, exactly as
 * `ForwardIcon` is drawn rather than flipped: four numbers against a CSS
 * transform that a right-to-left stylesheet would then have to know about.
 */
export function LessIcon(props) {
    return (
        <Icon {...props}>
            <path d="m5 10.5 7 7 7-7M5 5l7 7 7-7" />
        </Icon>
    );
}

/**
 * TOP SPEED — a speedometer wound to its stop.
 *
 * The needle points to the top right rather than straight up, because a dial at
 * its maximum is what the question is about. Straight up reads as idling.
 */
export function SpeedIcon(props) {
    return (
        <Icon {...props}>
            <path d="M2.5 19a9.5 9.5 0 0 1 19 0" />
            <path d="M2.5 19h19" />
            <path d="m12 19 6.5-8.5" />
        </Icon>
    );
}

/** POWER — a bolt. The one glyph in the set that needs no explanation anywhere. */
export function PowerIcon(props) {
    return (
        <Icon {...props}>
            <path d="M13.5 2.5 5 13.5h6l-.5 8L19 10.5h-6z" />
        </Icon>
    );
}

/**
 * CYLINDERS — a bank of bores, seen down the barrel.
 *
 * FOUR of them, in a row, inside a block. Four rather than three because the
 * question is HOW MANY and three of anything reads as a decorative triplet; a
 * row of four reads as a count that could have been a different number.
 *
 * A row rather than a V: a V draws a LAYOUT, which is a different fact the
 * catalogue keeps separately as `engineConfiguration`, and the two glyphs would
 * then be answering each other's question.
 */
export function CylindersIcon(props) {
    return (
        <Icon {...props}>
            <path d="M2.5 7.5h19v9h-19z" />
            <circle cx="6.5" cy="12" r="1.7" />
            <circle cx="10.5" cy="12" r="1.7" />
            <circle cx="14.5" cy="12" r="1.7" />
            <circle cx="18.5" cy="12" r="1.7" />
        </Icon>
    );
}

/**
 * ENGINE SIZE — a piston in its bore, with the stroke it sweeps.
 *
 * The arrow is what makes this a VOLUME rather than a part. Without it the glyph
 * was a stack of two rectangles sitting next to a glyph of four circles in a
 * box, and at 28px a reader had two engine drawings and no way to tell which was
 * "how many" and which was "how big". The bore is open at the top because the
 * swept space is the subject.
 */
export function DisplacementIcon(props) {
    return (
        <Icon {...props}>
            <path d="M6.5 2.5v19h11v-19" />
            <path d="M6.5 14.5h11v4h-11z" />
            <path d="M12 5.5v7m0-7 2 2m-2-2-2 2m2 7-2-2m2 2 2-2" />
        </Icon>
    );
}

/** TORQUE — a shaft being twisted. The arc is the turn; the head is the force. */
export function TorqueIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.5 12a7.5 7.5 0 1 1 3.6 6.4" />
            <path d="M4.5 6.5v5.5h5.5" />
            <circle cx="12" cy="12" r="2" />
        </Icon>
    );
}

/** 0–100 — a stopwatch. A stopwatch is a stopwatch in every language there is. */
export function StopwatchIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="13.5" r="7.5" />
            <path d="M12 13.5V9" />
            <path d="M9.5 2.5h5M12 2.5v3M18.5 6.5l2-2" />
        </Icon>
    );
}

/** WEIGHT — the block with a handle that every set of scales in the world uses. */
export function WeightIcon(props) {
    return (
        <Icon {...props}>
            <path d="M9 8.5a3 3 0 0 1 6 0" />
            <path d="M6.5 8.5h11l2 12h-15z" />
        </Icon>
    );
}

/** POWER TO WEIGHT — the two glyphs it is made of, over and under a rule. */
export function PowerToWeightIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12.5 2.5 8 8.5h3.2l-.7 4.5 4.5-6h-3.2z" />
            <path d="M3.5 13.5h17" />
            <path d="M8 21.5h8l-1.2-5h-5.6z" />
        </Icon>
    );
}

/** LENGTH — the measure between two ends, which is what a tape does. */
export function LengthIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.5 5.5v13M20.5 5.5v13" />
            <path d="M3.5 12h17" />
            <path d="m7 8.5-3.5 3.5 3.5 3.5M17 8.5l3.5 3.5-3.5 3.5" />
        </Icon>
    );
}

/**
 * THE QUARTER MILE — a strip running away from the reader, with the line across
 * the far end of it.
 *
 * Drawn in perspective, which is the whole legibility of it: a rectangle with a
 * bar across it is a door, and a shape that narrows as it goes up is a road. The
 * first drawing put a filled flag at the top and read as a tower with a lamp on
 * it — a chequered flag would have been clearer still and is already the tab's
 * own glyph, so it would have said "the duel" rather than "the quarter".
 */
export function QuarterMileIcon(props) {
    return (
        <Icon {...props}>
            <path d="M2.5 21.5 9 4.5h6l6.5 17" />
            <path d="M2.5 21.5h19" />
            <path d="M8 9.5h8" />
            <path d="M12 21.5v-5" />
        </Icon>
    );
}

/**
 * HOW MANY WERE BUILT — a cut stone.
 *
 * Rarity rather than a count, because the question is "which is rarer" and the
 * number underneath it is the wrong way round: fewer wins. A gem is the one
 * shape a child reads as "there are not many of these", where a factory or a
 * tally reads as "there are lots".
 */
export function RarityIcon(props) {
    return (
        <Icon {...props}>
            <path d="M7 3.5h10l4 6-9 11.5L3 9.5z" />
            <path d="M3 9.5h18M9 3.5l-2 6 5 11.5 5-11.5-2-6" />
        </Icon>
    );
}
